import { useRouter } from 'next/router';
import { env } from '@zopauk/nextjs-env';
import { logEvent } from '@/utils/analytics';
import { Button, Alert } from '@zopauk/web-ui';
import Events from '@/utils/amplitudeEvents.json';
import React, { useEffect, useState } from 'react';

interface IRatePageProps {
  selection: string;
  url: string;
}

const STORAGE_KEY = 'sf_page_rate_update';

const getVisitedPages = () => {
  const visitedPageJson = JSON.stringify({
    expiry: new Date(new Date().getTime() + 24 * 60 * 60 * 1000),
    pages: [],
  });
  return JSON.parse((typeof window !== 'undefined' && localStorage.getItem(STORAGE_KEY)) || visitedPageJson);
};

const updateVisitedPages = (page: IRatePageProps) => {
  const currentVisitedPages = getVisitedPages();
  currentVisitedPages.pages = [...currentVisitedPages.pages, page];
  localStorage.setItem(STORAGE_KEY, JSON.stringify(currentVisitedPages));
};

const checkStorageExpiry = () => {
  if (new Date(getVisitedPages().expiry) < new Date()) {
    localStorage.removeItem(STORAGE_KEY);
  }
  return getVisitedPages();
};

interface IErrors {
  maxLength?: boolean;
  required?: boolean;
}

const validate = (setErrors: (errors: IErrors) => void, feedback: string) => {
  const errors: IErrors = {};
  if (feedback === '') {
    errors.required = true;
  }
  if (feedback.length > 500) {
    errors.maxLength = true;
  }
  setErrors(errors);
  return !!Object.values(errors).length;
};

/**
 * Can only be used in the context of a Question model
 */
const Survey = () => {
  const { replace, asPath } = useRouter();
  const [visited, setVisitedPages] = useState(checkStorageExpiry());
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [errors, setErrors] = useState<IErrors>({});

  useEffect(() => {
    if (showFeedback) {
      document.getElementById('feedback')?.focus();
    }
  }, [showFeedback]);

  const visitedPage = visited.pages.find(({ url = '' }) => url === asPath);

  return (
    <div className="mb-8 lg:mb-11">
      <div className="px-5 mx-auto max-w-[700px]">
        <div className="bg-base-100 block rounded-lg px-7 py-6">
          {visitedPage ? (
            <div className="flex flex-col items-center">
              {visitedPage.selection === 'yes' ? (
                <p className="text-lg">Thank you for your feedback!</p>
              ) : (
                <>
                  <p className="mb-6 text-lg">Sorry we've not been able to help you so far.</p>
                  <Button
                    onClick={() => {
                      logEvent(Events.START_CHAT, {
                        id: 'chat-started-click',
                        page: asPath,
                      });
                      replace(env('NEXT_PUBLIC_APPSFLYER_CHAT_URL') || '');
                    }}
                    type="button"
                  >
                    Start chat
                  </Button>
                </>
              )}
            </div>
          ) : (
            <>
              {!showFeedback ? (
                <>
                  <div className="flex flex-col items-center">
                    <p className="mb-6">Was this helpful?</p>
                  </div>
                  <div className="flex justify-center">
                    <Button
                      onClick={() => {
                        updateVisitedPages({
                          selection: 'yes',
                          url: asPath,
                        });
                        setVisitedPages(getVisitedPages());
                        logEvent(Events.LIKE_FAQ, {
                          page: `${env('NEXT_PUBLIC_WEB_GALLERIA_URL')}/help/article/${asPath}`,
                          type: 'like',
                        });
                      }}
                      disabled={showFeedback}
                      className="mx-5"
                      type="button"
                    >
                      Yes
                    </Button>
                    <Button
                      onClick={() => {
                        setShowFeedback(true);
                      }}
                      disabled={showFeedback}
                      className="mx-5"
                      type="button"
                    >
                      No
                    </Button>
                  </div>
                </>
              ) : null}

              {showFeedback ? (
                <div className="relative">
                  <div className="flex flex-col items-center">
                    <label className="mb-5 block text-base font-medium text-primary" htmlFor="feedback">
                      Can you let us know in a quick summary?
                    </label>
                  </div>
                  <div className="relative">
                    <textarea
                      className="mb-8 rounded-[10px] block w-full p-4 resize-vertical bg-white border border-transparent focus:border-blue-500, color: bg-background"
                      onChange={(e) => setFeedback(e.currentTarget.value)}
                      value={feedback}
                      name="feedback"
                      id="feedback"
                      rows={5}
                    />
                    <span
                      className={`absolute right-5 -bottom-6 font-semibold text-sm ${feedback.length > 500 ? 'text-error-content' : 'text-primary'}`}
                    >
                      {feedback.length} / 500
                    </span>
                  </div>
                  {errors.maxLength ? (
                    <Alert className="mb-6" variant="error">
                      Your message cannot be longer than 500 characters in length
                    </Alert>
                  ) : null}
                  {errors.required ? (
                    <Alert className="mb-6" variant="error">
                      Please help us improve this article for everyone by providing us with some feedback
                    </Alert>
                  ) : null}
                  <div className="flex flex-col items-center">
                    <div>
                      <Button
                        onClick={() => {
                          setFeedback('');
                          setShowFeedback(false);
                        }}
                        variant="secondary"
                        className="mr-5"
                        outline
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={() => {
                          if (validate(setErrors, feedback)) {
                            return;
                          }
                          updateVisitedPages({
                            selection: 'no',
                            url: asPath,
                          });
                          setVisitedPages(getVisitedPages());
                          logEvent(Events.DISLIKE_FAQ, {
                            page: `${env('NEXT_PUBLIC_WEB_GALLERIA_URL')}/help/article/${asPath}`,
                            type: 'dislike',
                            feedback,
                          });
                        }}
                        disabled={!feedback.length}
                        type="button"
                      >
                        Submit
                      </Button>
                    </div>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Survey;
